// src/disc/DiscConfiguration.js
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Psychology as PsychologyIcon,
  Groups as GroupsIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderFields, { DEFAULT_REMINDER_CONFIG } from '../layouts/TestReminderFields';
import useStore from '../config/store';
import api from '../config/api';
import DiscTestBulkSend from './DiscTestBulkSend';

const applicantFilter = createFilterOptions({
  stringify: (option) => `${option?.name || ''}`,
  trim: true,
});

// Filtro explícito del selector de cargo: SOLO por nombre, insensible a
// mayúsculas y tildes. Usa el texto visible del campo (estado propio) para no
// depender del inputValue interno del Autocomplete.
const normalizeText = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const filterPositionsByName = (options, searchTerm) => {
  const term = normalizeText(searchTerm).trim();
  if (!term) return options;
  return options.filter((option) => normalizeText(option?.name).includes(term));
};

const DiscConfiguration = () => {
  const { t } = useTranslation();
  const { authToken, user, project, organization, showAlert, language } = useStore();

  // Estados del formulario
  const [testMode, setTestMode] = useState('simple');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positionSearchText, setPositionSearchText] = useState('');
  const [currentPosition, setCurrentPosition] = useState('');
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [reminderConfig, setReminderConfig] = useState(DEFAULT_REMINDER_CONFIG);

  // Estados de datos
  const [applicants, setApplicants] = useState([]);
  const [positions, setPositions] = useState([]);
  const [competencies, setCompetencies] = useState([]);

  // Estados de carga

  // Competencias predeterminadas del cargo (2026-09-09): al cambiar el cargo, la
  // selección se REEMPLAZA por las predeterminadas de ese cargo (o queda vacía si
  // no tiene / no hay cargo). Lo que el reclutador agregue o quite después aplica
  // solo a este envío; nunca modifica el cargo. Se resuelve contra el catálogo
  // cargado para reutilizar los mismos objetos del Autocomplete.
  const handlePositionChange = (newPosition) => {
    setSelectedPosition(newPosition);
    const defaults = Array.isArray(newPosition?.competencias) ? newPosition.competencias : [];
    setSelectedCompetencies(
      defaults
        .map((c) => {
          const id = c.competencyId ?? c.id ?? c.uid;
          const fromCatalog = competencies.find((opt) => (opt.id ?? opt.uid) === id);
          return fromCatalog || (id ? { id, name: c.competencyName ?? c.name ?? '' } : null);
        })
        .filter(Boolean)
    );
  };
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);
  const [sending, setSending] = useState(false);

  // Modo de envío: 'individual' o 'bulk'
  const [sendMode, setSendMode] = useState('individual');

  // Cargar postulantes
  useEffect(() => {
    const loadApplicants = async () => {
      if (!authToken || !project) return;

      try {
        setLoadingApplicants(true);
        const response = await api.get(
          `/applicants`,
          {
            params: { projectId: project },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.errorCode === 0 && response.data.response?.data) {
          const rawApplicants = response.data.response.data || [];

          const uniqueApplicants = Array.from(
            new Map(
              rawApplicants.map((applicant) => [
                applicant.id ?? applicant.uid ?? applicant.email,
                applicant,
              ])
            ).values()
          ).sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
          );

          setApplicants(uniqueApplicants);
        }
      } catch (error) {
        console.error('Error loading applicants:', error);
      } finally {
        setLoadingApplicants(false);
      }
    };

    loadApplicants();
  }, [authToken, project]);

  // Cargar cargos
  useEffect(() => {
    const loadPositions = async () => {
      if (!authToken || !project) return;

      try {
        setLoadingPositions(true);
        const response = await api.get(
          `/positions`,
          {
            params: { projectId: project },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.errorCode === 0 && response.data.response?.data) {
          setPositions(response.data.response.data);
        }
      } catch (error) {
        console.error('Error loading positions:', error);
      } finally {
        setLoadingPositions(false);
      }
    };

    loadPositions();
  }, [authToken, project]);

  // Cargar competencias cuando se selecciona modo completo
  useEffect(() => {
    const loadCompetencies = async () => {
      if (!authToken || !organization) return;

      try {
        setLoadingCompetencies(true);
        const response = await api.get(
          '/capabilities',
          {
            params: {
              language,
              organizationId: organization
            },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.data.errorCode === 0 && response.data.response?.data) {
          setCompetencies(response.data.response.data);
        }
      } catch (error) {
        console.error('Error loading competencies:', error);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    if (testMode === 'complete') {
      loadCompetencies();
    }
  }, [authToken, language, testMode, organization]);

  // Manejar envío del formulario
  const handleSendTest = async () => {
    if (!selectedApplicant) {
      showAlert(t('disc.pleaseSelectApplicant'), 'warning');
      return;
    }

    try {
      setSending(true);

      const testData = {
        applicantId: selectedApplicant.id || selectedApplicant.uid,
        applicantName: selectedApplicant.name,
        applicantEmail: selectedApplicant.email,
        mode: testMode,
        projectId: project,
        organizationId: organization,
        language: language,
        sendEmail: sendEmail,
        reminderConfig,
      };

      if (testMode === 'complete') {
        if (selectedPosition) {
          testData.positionId = selectedPosition.id ?? selectedPosition.uid;
          testData.positionName = selectedPosition.name;
          testData.positionDescription = selectedPosition.descripcion.trim();
        }

        if (currentPosition.trim()) {
          testData.currentPosition = currentPosition.trim();
        }

        if (selectedCompetencies.length > 0) {
          testData.competencies = selectedCompetencies.map(c => ({
            competencyId: c.id ?? c.uid,
            competencyName: c.name ?? '',
          }));
        }
      }

      const response = await api.post(
        '/disc-tests',
        testData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.errorCode === 0) {
        showAlert(
          sendEmail ? t('disc.testSentSuccess') : t('disc.testCreatedSuccess'),
          'success'
        );
        resetForm();
      } else {
        showAlert(response.data.errorMessage || t('disc.testSentError'), 'error');
      }
    } catch (error) {
      console.error('Error sending DISC test:', error);
      const errorMessage = error.response?.data?.response?.message || t('disc.testSentError');
      showAlert(errorMessage, 'error');
    } finally {
      setSending(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setTestMode('simple');
    setSelectedApplicant(null);
    setSelectedPosition(null);
    setCurrentPosition('');
    setSelectedCompetencies([]);
    setSendEmail(true);
    setReminderConfig(DEFAULT_REMINDER_CONFIG);
  };

  return (
    <Box>
      {/* Selector de modo de envío */}
      {user.rol === "super-admin" && <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={sendMode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode !== null) {
              setSendMode(newMode);
              resetForm();
            }
          }}
          aria-label="modo de envío"
        >
          <ToggleButton value="individual" aria-label="envío individual">
            <PersonIcon sx={{ mr: 1 }} />
            {t('disc.bulkSend.individualSend')}
          </ToggleButton>
          <ToggleButton value="bulk" aria-label="envío masivo">
            <GroupsIcon sx={{ mr: 1 }} />
            {t('disc.bulkSend.bulkSend')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>}

      {/* Formulario de envío individual */}
      {sendMode === 'individual' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t('disc.configurationTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('disc.configurationSubTitle')}
          </Typography>

          {/* Selector de modo */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('disc.testMode')}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={testMode}
                onChange={(e) => setTestMode(e.target.value)}
              >
                <FormControlLabel
                  value="simple"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {t('disc.simpleMode')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('disc.simpleModeDescription')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="complete"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {t('disc.completeMode')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('disc.completeModeDescription')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Selector de postulante */}
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={applicants}
              getOptionLabel={(option) => option?.name || ''}
              getOptionKey={(option) => option.id ?? option.uid ?? option.email}
              isOptionEqualToValue={(option, value) =>
                (option.id ?? option.uid ?? option.email) ===
                (value.id ?? value.uid ?? value.email)
              }
              filterOptions={applicantFilter}
              value={selectedApplicant}
              onChange={(event, newValue) => setSelectedApplicant(newValue)}
              loading={loadingApplicants}
              noOptionsText={t('noData')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('disc.selectApplicant')}
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                    endAdornment: (
                      <>
                        {loadingApplicants ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id ?? option.uid ?? option.email}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>

          {/* Campos adicionales para modo completo */}
          {testMode === 'complete' && (
            <>
              {/* Selector de cargo */}
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={positions}
                  getOptionLabel={(option) => option.name || ''}
                  getOptionKey={(option) => option.uid ?? option.id}
                  isOptionEqualToValue={(option, value) =>
                    (option.uid ?? option.id) === (value.uid ?? value.id)
                  }
                  filterOptions={(opts) => filterPositionsByName(opts, positionSearchText)}
                  onInputChange={(event, newInputValue) => setPositionSearchText(newInputValue)}
                  value={selectedPosition}
                  onChange={(event, newValue) => handlePositionChange(newValue)}
                  loading={loadingPositions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t('disc.selectPosition')} ${t('disc.optional')}`}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active' }} />,
                        endAdornment: (
                          <>
                            {loadingPositions ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.uid ?? option.id}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.industria}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Box>

              {/* Campo de puesto actual */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label={`${t('disc.currentPosition')} ${t('disc.optional')}`}
                  placeholder={t('disc.currentPositionPlaceholder')}
                  value={currentPosition}
                  onChange={(e) => setCurrentPosition(e.target.value)}
                />
              </Box>

              {/* Selector de competencias */}
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  multiple
                  options={competencies}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedCompetencies}
                  onChange={(event, newValue) => setSelectedCompetencies(newValue)}
                  isOptionEqualToValue={(option, value) => (option.id ?? option.uid) === (value.id ?? value.uid)}
                  loading={loadingCompetencies}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={option.name}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t('disc.selectCompetencies')} ${t('disc.optional')}`}
                      helperText={t('disc.competenciesDescription')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <PsychologyIcon sx={{ mr: 1, color: 'action.active' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingCompetencies ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </>
          )}

          {/* Switch enviar email */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
            <EmailIcon sx={{ color: sendEmail ? 'primary.main' : 'action.disabled' }} />
            <FormControlLabel
              control={
                <Switch
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {t('disc.sendEmailOnCreate')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sendEmail
                      ? t('disc.sendEmailOnCreateEnabled')
                      : t('disc.sendEmailOnCreateDisabled')
                    }
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Recordatorio automatico: solo aplica cuando el correo lo manda el sistema */}
          <TestReminderFields
            show={sendEmail}
            value={reminderConfig}
            onChange={setReminderConfig}
          />

          {/* Botones de acción */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={sending}
            >
              {t('disc.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSendTest}
              disabled={sending || !selectedApplicant}
            >
              {sending
                ? t('sending')
                : sendEmail
                  ? t('disc.sendTest')
                  : t('disc.createTest')
              }
            </Button>
          </Box>
        </Paper>
      )}

      {/* Componente de envío masivo */}
      {sendMode === 'bulk' && (
        <DiscTestBulkSend
          applicants={applicants}
        />
      )}

      {/* Información adicional para modo individual */}
      {sendMode === 'individual' && (
        <Alert severity="info">
          <Typography variant="body2">
            {testMode === 'simple'
              ? t('disc.simpleModeDescription')
              : t('disc.completeModeDescription')}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DiscConfiguration;